import React, { useEffect, useRef, useState } from "react";
import { fechaMX } from "@/lib/format";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Upload, Eye, Download, Trash2, FileText } from "lucide-react";

const OTRO = "Otro";

export default function DocumentosSection({ entidadTipo, entidadId, tipos = [], canManage = false }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tipo, setTipo] = useState("");
  const [tipoOtro, setTipoOtro] = useState("");
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const fileRef = useRef(null);

  const fetchDocs = async () => {
    if (!entidadId) { setDocs([]); setLoading(false); return; }
    setLoading(true);
    try {
      const { data } = await api.get(`/documentos`, { params: { entidad_tipo: entidadTipo, entidad_id: entidadId } });
      setDocs(data || []);
    } catch (err) { /* silencioso */ } finally { setLoading(false); }
  };
  useEffect(() => { fetchDocs(); /* eslint-disable-next-line */ }, [entidadTipo, entidadId]);

  const subir = async () => {
    const file = fileRef.current?.files?.[0];
    const tipoFinal = tipo === OTRO ? tipoOtro.trim() : tipo;
    if (!tipoFinal) return toast.error("Selecciona el tipo de documento.");
    if (!file) return toast.error("Selecciona un archivo.");
    const okType = ["application/pdf", "image/jpeg", "image/png"].includes(file.type);
    if (!okType) return toast.error("Solo se permiten archivos PDF, JPG o PNG.");
    if (file.size > 15 * 1024 * 1024) return toast.error("El archivo supera el límite de 15 MB.");
    const fd = new FormData();
    fd.append("entidad_tipo", entidadTipo);
    fd.append("entidad_id", entidadId);
    fd.append("tipo_documento", tipoFinal);
    fd.append("file", file);
    setUploading(true); setProgress(0);
    try {
      await api.post("/documentos", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => setProgress(Math.round((e.loaded * 100) / (e.total || e.loaded))),
      });
      toast.success("Documento subido correctamente.");
      setTipo(""); setTipoOtro(""); if (fileRef.current) fileRef.current.value = "";
      fetchDocs();
    } catch (err) {
      toast.error(`Error al subir: ${err?.response?.data?.detail || err.message}`);
    } finally { setUploading(false); setProgress(0); }
  };

  const abrirBlob = async (doc, descargar = false) => {
    try {
      const { data } = await api.get(`/documentos/${doc.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      if (descargar) {
        const a = document.createElement("a");
        a.href = url; a.download = doc.nombre_archivo; document.body.appendChild(a); a.click(); a.remove();
      } else {
        window.open(url, "_blank");
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) { toast.error(`No se pudo abrir el documento: ${err?.response?.data?.detail || err.message}`); }
  };

  const eliminar = async (id) => {
    if (!window.confirm("¿Eliminar este documento?")) return;
    try { await api.delete(`/documentos/${id}`); toast.success("Documento eliminado."); fetchDocs(); }
    catch (err) { toast.error(`Error: ${err?.response?.data?.detail || err.message}`); }
  };

  if (!entidadId) {
    return <p className="text-sm text-muted-foreground">Guarda primero el registro para poder adjuntar documentos.</p>;
  }

  return (
    <div className="space-y-4" data-testid="documentos-section">
      {canManage && (
        <div className="flex flex-col md:flex-row items-end gap-3 bg-muted/40 p-3 rounded-md">
          <div className="space-y-2 w-full md:w-64">
            <Label>Tipo de documento</Label>
            <Select value={tipo} onValueChange={setTipo}>
              <SelectTrigger data-testid="doc-tipo-select"><SelectValue placeholder="Seleccione tipo" /></SelectTrigger>
              <SelectContent>
                {tipos.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                <SelectItem value={OTRO}>Otro (especificar)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {tipo === OTRO && (
            <div className="space-y-2 w-full md:w-56">
              <Label>Nombre del documento</Label>
              <Input value={tipoOtro} onChange={(e) => setTipoOtro(e.target.value)} placeholder="Ej. Acta constitutiva" data-testid="doc-tipo-otro" />
            </div>
          )}
          <div className="space-y-2 flex-1">
            <Label>Archivo (PDF, JPG o PNG · máx 15 MB)</Label>
            <Input type="file" accept=".pdf,image/jpeg,image/png" ref={fileRef} data-testid="doc-file-input" />
          </div>
          <Button type="button" onClick={subir} disabled={uploading} data-testid="doc-upload-btn">
            <Upload className="h-4 w-4 mr-2" /> {uploading ? `Subiendo ${progress}%` : "Subir"}
          </Button>
        </div>
      )}

      <div className="border rounded-md">
        <Table>
          <TableHeader><TableRow><TableHead>Tipo</TableHead><TableHead>Archivo</TableHead><TableHead>Fecha</TableHead><TableHead className="text-right w-40">Acciones</TableHead></TableRow></TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={4} className="text-center h-16 text-muted-foreground">Cargando...</TableCell></TableRow>
            ) : docs.length === 0 ? (
              <TableRow><TableCell colSpan={4} className="text-center h-16 text-muted-foreground">Sin documentos.</TableCell></TableRow>
            ) : docs.map((d) => (
              <TableRow key={d.id} data-testid={`doc-row-${d.id}`}>
                <TableCell className="font-medium">{d.tipo_documento}</TableCell>
                <TableCell className="text-sm"><span className="inline-flex items-center gap-1"><FileText className="h-3.5 w-3.5" /> {d.nombre_archivo}</span></TableCell>
                <TableCell className="text-sm">{fechaMX(d.created_at)}</TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    <Button variant="ghost" size="icon" title="Ver" onClick={() => abrirBlob(d, false)} data-testid={`doc-view-${d.id}`}><Eye className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="icon" title="Descargar" onClick={() => abrirBlob(d, true)} data-testid={`doc-download-${d.id}`}><Download className="h-4 w-4" /></Button>
                    {canManage && <Button variant="ghost" size="icon" title="Eliminar" className="text-destructive" onClick={() => eliminar(d.id)} data-testid={`doc-delete-${d.id}`}><Trash2 className="h-4 w-4" /></Button>}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
